import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, VIEW_MODES } from "../context/AuthContext";
import { Button } from "./ui/button";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { 
  Menu, X, Settings, LogOut, User, LayoutDashboard, Leaf, Route, FileText, 
  FolderOpen, DollarSign, ClipboardList, Shield, Users, Building2, ChevronDown, ChevronRight,
  Book, Newspaper, Phone, BarChart3, Mail, Zap, Database, Wallet, MessageSquare,
  GripVertical, Plus, Trash2, Star, Pin, Container, Calendar, Truck, Download, Target, Award, Search, Scale, Landmark, TrendingUp, CreditCard, Receipt, Activity, Headphones, Linkedin,
  Smartphone, Mic, Train, Globe, ListChecks, RefreshCw, Key, History, Bot, UserPlus, ShieldCheck
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ReportProblemButton from "./ReportProblemButton";

// myEuropacific logo
const MYEUROPACIFIC_LOGO = "https://customer-assets.emergentagent.com/job_65c103ce-390f-4bae-b6d5-2798d8bc6281/artifacts/6in60tne_image.png";

// View mode configurations
const VIEW_MODE_CONFIG = {
  [VIEW_MODES.GOD]: {
    label: "God Mode",
    icon: Shield,
    color: "bg-gradient-to-r from-purple-600 to-pink-600",
    textColor: "text-white",
  },
  [VIEW_MODES.STAFF]: {
    label: "Staff View",
    icon: Building2,
    color: "bg-blue-600",
    textColor: "text-white",
  },
  [VIEW_MODES.CUSTOMER]: {
    label: "Customer View",
    icon: Users,
    color: "bg-green-600",
    textColor: "text-white",
  },
};

// All available modules with their metadata
const ALL_MODULES = {
  tracking: { path: "/tracking", label: "Tracking", icon: LayoutDashboard, category: "core" },
  "customer-portal": { path: "/customer-portal", label: "My Shipments", icon: FolderOpen, category: "customer", customerOnly: true },
  "customer-quotes": { path: "/customer-quotes", label: "Get Quote", icon: DollarSign, category: "customer", customerOnly: true },
  "my-quotes-customer": { path: "/my-quotes-customer", label: "My Quotes", icon: ClipboardList, category: "customer", customerOnly: true },
  "route-calculator": { path: "/route-calculator", label: "Route & Transit Calculator", icon: Route, category: "sales", hideFromCustomer: true },
  "carrier-approvals": { path: "/carrier-approvals", label: "Carrier Approvals", icon: Truck, category: "operations", hideFromCustomer: true },
  "customer-approvals": { path: "/customer-approvals", label: "Customer Approvals", icon: UserPlus, category: "operations", staffOnly: true },
  "partner-approvals": { path: "/partner-approvals", label: "Partner Approvals", icon: ShieldCheck, category: "operations", staffOnly: true },
  "my-company": { path: "/my-company", label: "My Company & Contacts", icon: Building2, category: "customer", customerOnly: true },
  "my-finance": { path: "/my-finance", label: "My Finance", icon: Wallet, category: "customer", customerOnly: true },
  directory: { path: "/directory", label: "Europacific Contacts", icon: Phone, category: "customer", customerOnly: true },
  "customer-feedback": { path: "/customer-feedback", label: "Feedback & Suggestions", icon: MessageSquare, category: "customer", customerOnly: true },
  chat: { path: "/chat", label: "AI Assistant", icon: MessageSquare, category: "tools" },
  "co2-calculator": { path: "/co2-calculator", label: "CO2 Calculator", icon: Leaf, category: "tools" },
  "co2-dashboard":  { path: "/co2-dashboard",  label: "CO2 Dashboard",  icon: Leaf, category: "tools" },
  "load-calculator": { path: "/load-calculator", label: "Load Calculator", icon: Container, category: "tools" },
  "voice-notes": { path: "/voice-notes", label: "Voice Notes", icon: Mic, category: "tools", staffOnly: true },
  "user-guide": { path: "/guide", label: "User Guide", icon: Book, category: "tools" },
  "apis": { path: "/integrations", label: "APIs", icon: Zap, category: "admin", staffOnly: true },
  "cma-cgm": { path: "/searates", label: "SeaRates", icon: Container, category: "sales", staffOnly: true },
  offers: { path: "/offers", label: "Offers", icon: BarChart3, category: "sales", staffOnly: true },
  "customer-advisory": { path: "/customer-advisory", label: "Customer Advisory", icon: Mail, category: "sales", staffOnly: true },
  "customer-reactivation": { path: "/reactivation", label: "Reactivation", icon: RefreshCw, category: "sales", staffOnly: true },
  "whatsapp-tickets": { path: "/whatsapp-tickets", label: "WhatsApp Tickets", icon: MessageSquare, category: "operations", staffOnly: true },
  "whatsapp-users": { path: "/whatsapp/users", label: "WhatsApp Users", icon: Users, category: "operations", staffOnly: true },
  "support-queue": { path: "/support-queue", label: "Support Queue", icon: Headphones, category: "operations", staffOnly: true },
  "task-templates": { path: "/task-templates", label: "Task Admin", icon: ListChecks, category: "operations", staffOnly: true },
  "sms-management": { path: "/sms-management", label: "SMS", icon: Phone, category: "operations", adminOnly: true },
  "sales-leads": { path: "/sales-leads", label: "Lead Engine", icon: Target, category: "sales", staffOnly: true },
  "email-repository": { path: "/email-repository", label: "Email Inbox", icon: Mail, category: "operations", staffOnly: true },
  crm: { path: "/crm", label: "CRM", icon: Users, category: "operations", staffOnly: true },
  "partner-directory": { path: "/partner-directory", label: "Partner Networks", icon: Globe, category: "operations", staffOnly: true },
  bl: { path: "/bl", label: "BL", icon: FileText, category: "operations", staffOnly: true },
  trinet: { path: "/trinet", label: "Trinet", icon: Zap, category: "operations", staffOnly: true },
  "position-folders": { path: "/position-folders", label: "Orders (PF)", icon: FolderOpen, category: "operations", staffOnly: true },
  "transport-orders": { path: "/transport-orders", label: "Transport", icon: FileText, category: "operations", staffOnly: true },
  "trucking-blacklist": { path: "/trucking-blacklist", label: "Blacklist", icon: Shield, category: "operations", staffOnly: true },
  "trucking-portal": { path: "/trucking-portal", label: "Tender Portal", icon: Truck, category: "operations", staffOnly: true, truckingOnly: true },
  // Finance Section - Flat list (no subgroups)
  "vendor-ap": { path: "/vendor-ap", label: "Vendor / AP Intelligence", icon: DollarSign, category: "finance", staffOnly: true },
  "finance": { path: "/finance", label: "A/R & Credits", icon: Wallet, category: "finance", staffOnly: true },
  "ar-ap": { path: "/ar-ap", label: "AR/AP by Vertical", icon: Receipt, category: "finance", staffOnly: true },
  "payment-links": { path: "/payment-links", label: "Stripe Payments", icon: CreditCard, category: "finance", staffOnly: true },
  "cashflow-dashboard": { path: "/cashflow-dashboard", label: "Cash Flow Pro", icon: Activity, category: "finance", staffOnly: true },
  "bank-status": { path: "/bank-status", label: "Bank Balances", icon: Landmark, category: "finance", staffOnly: true },
  "loans": { path: "/loans", label: "Bank Loans", icon: CreditCard, category: "finance", staffOnly: true },
  "cashflow": { path: "/cashflow", label: "Salaries & VAT", icon: TrendingUp, category: "finance", staffOnly: true },
  "izvrsbe": { path: "/izvrsbe", label: "Izvršbe (Enforcements)", icon: Scale, category: "finance", staffOnly: true },
  "shareholder-reports": { path: "/shareholder-reports", label: "Shareholder Reports", icon: BarChart3, category: "finance", staffOnly: true },
  // HR & Compliance
  staff: { path: "/staff", label: "Staff", icon: Users, category: "hr", staffOnly: true },
  "leave-calendar": { path: "/leave-calendar", label: "Staff Calendar", icon: Calendar, category: "hr", staffOnly: true },
  "hr-performance": { path: "/hr-performance", label: "Performance", icon: Award, category: "hr", adminOnly: true },
  "my-feedback": { path: "/my-feedback", label: "My Feedback", icon: MessageSquare, category: "hr", adminOnly: true },
  "satisfaction-reviews": { path: "/satisfaction-reviews", label: "Service Reviews", icon: Star, category: "hr", adminOnly: true },
  "customer-nps": { path: "/customer-nps", label: "Customer NPS", icon: TrendingUp, category: "hr", adminOnly: true },
  hrbot: { path: "/hrbot", label: "HRBot", icon: Bot, category: "hr", adminOnly: true },
  compliance: { path: "/compliance", label: "ComplianceBot", icon: Shield, category: "hr", adminOnly: true },
  "operations-monitor": { path: "/operations-monitor", label: "Operations Control", icon: Target, category: "hr", staffOnly: true },
  "pa-operations": { path: "/pa-operations", label: "PA Operations", icon: Zap, category: "operations", staffOnly: true },
  // Communications
  news: { path: "/news", label: "News", icon: Newspaper, category: "comms" },
  newsletter: { path: "/newsletter", label: "Newsletter", icon: Mail, category: "comms", adminOnly: true },
  "linkedin-newsletter": { path: "/linkedin-newsletter", label: "LinkedIn", icon: Linkedin, category: "comms", adminOnly: true },
  "unsubscribe-dashboard": { path: "/unsubscribe-dashboard", label: "Unsubscribe Dashboard", icon: Shield, category: "comms", adminOnly: true },
  "health-dashboard": { path: "/health", label: "Integration Health", icon: Activity, category: "admin", staffOnly: true },
  "system-monitor": { path: "/system-monitor", label: "System Monitor", icon: Activity, category: "admin", adminOnly: true },
  "llm-cost-control": { path: "/llm-cost-control", label: "LLM Cost Control", icon: DollarSign, category: "admin", adminOnly: true },
  "email-archive": { path: "/email-archive", label: "Email Archive", icon: Mail, category: "sales", staffOnly: true },
  admin: { path: "/admin", label: "Admin", icon: Settings, category: "admin", adminOnly: true },
  "user-management": { path: "/user-management", label: "User Management", icon: Shield, category: "admin", adminOnly: true },
  "deployment-log": { path: "/deployment-log", label: "Deployment Log", icon: History, category: "admin", adminOnly: true },
  "weekly-reports": { path: "/weekly-reports", label: "Weekly Reports", icon: Mail, category: "admin", adminOnly: true },
  "weekly-trends": { path: "/weekly-trends", label: "Weekly Trends", icon: TrendingUp, category: "admin", adminOnly: true },
  "email-rules": { path: "/email-rules", label: "Email Rules", icon: Mail, category: "admin", adminOnly: true },
  "puk-management": { path: "/admin/puk-management", label: "PUK Management", icon: Key, category: "admin", adminOnly: true },
  "import-export": { path: "/import-export", label: "Import/Export", icon: Database, category: "admin", adminOnly: true },
  "staff-mobile-preview": { path: "/staff-mobile", label: "Staff Mobile App", icon: Smartphone, category: "admin", adminOnly: true },
  "customer-mobile-preview": { path: "/mobile", label: "Customer Mobile App", icon: Smartphone, category: "admin", adminOnly: true },
};

const CATEGORY_LABELS = {
  core: "Core",
  customer: "Customer Portal",
  tools: "Tools",
  sales: "Sales & Quotes",
  operations: "Operations",
  finance: "Finance",
  hr: "HR & Compliance",
  comms: "Communications",
  admin: "Admin",
};

// Finance sub-groups removed - now flat list

export const Navbar = () => {
  const { user, logout, isAdmin, canUseGodMode, viewMode, setViewMode, getEffectiveView } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pinnedModules, setPinnedModules] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const quickAccessRef = useRef(null);
  
  // Collapsible category state - load from localStorage or default to all expanded
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const saved = localStorage.getItem('sidebar_expanded_categories');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default: core and tools expanded, others collapsed for staff
    return {
      core: true,
      customer: true,
      tools: true,
      sales: false,
      operations: false,
      finance: false,
      hr: false,
      admin: false,
      o365: false,
    };
  });

  const effectiveView = getEffectiveView();
  const currentConfig = VIEW_MODE_CONFIG[effectiveView];
  const actualUserType = user?.user_type || "customer";
  const actualIsStaff = actualUserType === "europacific_staff";
  
  // Unread counts for badges
  const [unreadCounts, setUnreadCounts] = useState({});
  const token = localStorage.getItem("token");
  
  // Fetch unread counts for badges
  const fetchUnreadCounts = useCallback(async () => {
    if (!token || !actualIsStaff) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [notif, waTickets, custApps, carrApps] = await Promise.allSettled([
        axios.get(`${API_URL}/notifications/unread-counts`, { headers }),
        axios.get(`${API_URL}/whatsapp/tickets?status=pending&limit=1`, { headers }),
        axios.get(`${API_URL}/customer-registration?limit=1`, { headers }),
        axios.get(`${API_URL}/carrier-vendors?limit=1`, { headers }),
      ]);      const counts = notif.status === "fulfilled" ? (notif.value.data || {}) : {};
      if (waTickets.status === "fulfilled") {
        const pending = waTickets.value.data?.stats?.pending || 0;
        if (pending > 0) counts["whatsapp-tickets"] = pending;
      }
      // Registrations awaiting action (anything not yet approved/declined) → red badge
      const pendingFromCounts = (sc) => Object.entries(sc || {}).reduce(
        (n, [k, v]) => (["approved", "rejected", "declined"].includes(k) ? n : n + (v || 0)), 0);
      if (custApps.status === "fulfilled") {
        const p = pendingFromCounts(custApps.value.data?.status_counts);
        if (p > 0) counts["customer-approvals"] = p;
      }
      if (carrApps.status === "fulfilled") {
        const p = pendingFromCounts(carrApps.value.data?.status_counts);
        if (p > 0) counts["carrier-approvals"] = p;
      }
      try {
        const partnerRes = await axios.get(`${API_URL}/partner-portal/admin/partners`, { headers });
        const p = pendingFromCounts(partnerRes.data?.status_counts);
        if (p > 0) counts["partner-approvals"] = p;
      } catch (_) { /* ignore */ }
      setUnreadCounts(counts);
    } catch (error) {
      console.error("Failed to fetch unread counts:", error);
    }
  }, [token, actualIsStaff]);
  
  // Fetch unread counts on mount and every 60 seconds
  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  // Save expanded categories to localStorage
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newState = { ...prev, [category]: !prev[category] };
      localStorage.setItem('sidebar_expanded_categories', JSON.stringify(newState));
      return newState;
    });
  };

  // Load pinned modules from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`pinned_modules_${user?.id || 'guest'}`);
    if (saved) {
      setPinnedModules(JSON.parse(saved));
    } else {
      // Default pinned modules
      const defaults = actualIsStaff 
        ? ["position-folders", "crm", "tracking", "offers"]
        : ["tracking", "customer-portal", "my-finance", "chat"];
      setPinnedModules(defaults);
    }
  }, [user?.id, actualIsStaff]);

  // Save pinned modules to localStorage
  const savePinnedModules = (modules) => {
    setPinnedModules(modules);
    localStorage.setItem(`pinned_modules_${user?.id || 'guest'}`, JSON.stringify(modules));
  };

  // Get available modules based on user type and view
  const getAvailableModules = () => {
    const showAsCustomer = effectiveView === VIEW_MODES.CUSTOMER || (!actualIsStaff && effectiveView !== VIEW_MODES.GOD);
    const showAsStaff = effectiveView === VIEW_MODES.STAFF || effectiveView === VIEW_MODES.GOD || (actualIsStaff && effectiveView !== VIEW_MODES.CUSTOMER);
    const showAdminFeatures = isAdmin && (effectiveView === VIEW_MODES.GOD || effectiveView === VIEW_MODES.STAFF);
    
    // Get user's module permissions (if any)
    const userPermissions = user?.module_permissions || [];
    const hasAllPermissions = isAdmin || userPermissions.length === 0; // Admins have all permissions, empty means legacy user with all access

    return Object.entries(ALL_MODULES).filter(([key, mod]) => {
      if (mod.adminOnly && !showAdminFeatures) return false;
      if (mod.staffOnly && !showAsStaff) return false;
      // Hide customer-only items from staff (unless in GOD mode)
      if (mod.customerOnly && showAsStaff && effectiveView !== VIEW_MODES.GOD) return false;
      if (mod.category === "customer" && !showAsCustomer && effectiveView !== VIEW_MODES.GOD) return false;
      // Hide specific tools (e.g. Transit Times) from the customer view
      if (mod.hideFromCustomer && showAsCustomer && effectiveView !== VIEW_MODES.GOD) return false;
      
      // Check module permissions for staff users (skip for admins and GOD mode)
      if (actualIsStaff && !isAdmin && effectiveView !== VIEW_MODES.GOD && userPermissions.length > 0) {
        // Check if the specific module key is in permissions
        if (userPermissions.includes(key)) return true;
        
        // Check if the module's category is in permissions
        if (userPermissions.includes(mod.category)) return true;
        
        // Some modules might be mapped to a general permission
        const permissionMappings = {
          // Tools are available to all staff
          'route-calculator': true,
          'chat': true,
          'co2-calculator': true,
          'co2-dashboard':  'tools',
          'load-calculator': true,
          'user-guide': true,
          'tracking': true,
          // Map specific modules to their categories
          'offers': 'sales',
          'customer-advisory': 'sales',
          'customer-reactivation': 'sales',
          'sales-leads': 'sales',
          'email-archive': 'sales',
          'rate-finder': 'sales',
          'email-repository': 'operations',
          'whatsapp-tickets': 'operations',
          'whatsapp-users': 'operations',
          'crm': 'operations',
          'partner-directory': 'operations',
          'bl': 'operations',
          'trinet': 'operations',
          'position-folders': 'operations',
          'transport-orders': 'operations',
          'trucking-blacklist': 'operations',
          'trucking-portal': 'operations',
          'customer-groups': 'operations',
          'vendor-ap': 'finance',
          'finance': 'finance',
          'ar-ap': 'finance',
          'cashflow-dashboard': 'finance',
          'bank-status': 'finance',
          'loans': 'finance',
          'cashflow': 'finance',
          'izvrsbe': 'finance',
          'shareholder-reports': 'finance',
          'staff': 'hr',
          'leave-calendar': 'hr',
          'hr-performance': 'hr',
          'my-feedback': 'hr',
          'satisfaction-reviews': 'hr',
          'news': 'comms',
          'newsletter': 'comms',
          'linkedin-newsletter': 'comms',
          'unsubscribe-dashboard': 'comms',
          'hrbot': 'hr',
          'compliance': 'hr',
          'voice-notes': 'operations',
          'sms-management': 'operations',
          'support-queue': 'operations',
          'task-templates': 'operations',
          'apis': 'admin',
          'health-dashboard': 'admin',
          'system-monitor': 'admin',
          'admin': 'admin',
          'user-management': 'admin',
          'import-export': 'admin',
          'staff-mobile-preview': 'admin',
          'customer-mobile-preview': 'admin',
        };
        
        // If this is a tool/core module, allow it
        if (permissionMappings[key] === true) return true;
        
        // Check mapped permission
        const mappedCategory = permissionMappings[key];
        if (mappedCategory && userPermissions.includes(mappedCategory)) return true;
        
        // Not in permissions - hide it
        return false;
      }
      
      return true;
    }).map(([key, mod]) => ({ key, ...mod }));
  };

  const availableModules = getAvailableModules();

  // Group modules by category
  const modulesByCategory = availableModules.reduce((acc, mod) => {
    if (!acc[mod.category]) acc[mod.category] = [];
    acc[mod.category].push(mod);
    return acc;
  }, {});

  // Drag handlers
  const handleDragStart = (e, moduleKey) => {
    setDraggedItem(moduleKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (draggedItem && !pinnedModules.includes(draggedItem)) {
      const newPinned = [...pinnedModules, draggedItem];
      if (newPinned.length <= 8) { // Max 8 pinned
        savePinnedModules(newPinned);
      }
    }
    setDraggedItem(null);
  };

  const handleUnpin = (moduleKey) => {
    savePinnedModules(pinnedModules.filter(k => k !== moduleKey));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    setMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-sm"
      >
        <div className="max-w-full mx-auto px-3">
          <div className="flex items-center justify-between h-12">
            {/* Left: Hamburger + Logo */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMenuOpen(true)}
                className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-700"
                data-testid="hamburger-menu"
              >
                <Menu className="h-5 w-5" />
                {((unreadCounts["customer-approvals"] || 0) + (unreadCounts["carrier-approvals"] || 0) + (unreadCounts["partner-approvals"] || 0)) > 0 && (
                  <span
                    className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"
                    title="Registrations awaiting your approval"
                    data-testid="approvals-action-dot"
                  />
                )}
              </button>
              <Link to="/dashboard" className="flex items-center" data-testid="nav-logo">
                <img src={MYEUROPACIFIC_LOGO} alt="myEuropacific" className="h-8" />
              </Link>
            </div>

            {/* Center: Quick Access Toolbar (Pinned Modules) — hidden in Customer View */}
            {effectiveView !== VIEW_MODES.CUSTOMER ? (
            <div 
              ref={quickAccessRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`hidden md:flex items-center gap-1 flex-1 mx-4 px-2 py-1 rounded-lg min-h-[36px] transition-colors ${
                isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-400' : 'bg-slate-50'
              }`}
            >
              {pinnedModules.map((moduleKey) => {
                const mod = ALL_MODULES[moduleKey];
                if (!mod) return null;
                const Icon = mod.icon;
                const isActive = location.pathname === mod.path;
                const unreadCount = unreadCounts[moduleKey] || 0;
                return (
                  <div key={moduleKey} className="group relative">
                    <Link
                      to={mod.path}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all ${
                        isActive
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-slate-700 hover:bg-white hover:shadow-sm"
                      }`}
                    >
                      <div className="relative">
                        <Icon className="h-3.5 w-3.5" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </div>
                      <span className="hidden lg:inline">{mod.label}</span>
                      {unreadCount > 0 && (
                        <span className="hidden lg:inline bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Link>
                    <button
                      onClick={() => handleUnpin(moduleKey)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      title="Unpin"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                );
              })}
              {pinnedModules.length === 0 && (
                <span className="text-xs text-slate-400 px-2">Drag modules here for quick access</span>
              )}
              {isDragOver && (
                <span className="text-xs text-blue-600 px-2 animate-pulse">Drop to pin</span>
              )}
            </div>
            ) : null}

            {/* Right: View Mode + User Menu */}
            <div className="flex items-center gap-2">
              {/* God Mode Switcher */}
              {canUseGodMode && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${currentConfig.color} ${currentConfig.textColor} hover:opacity-90 h-7`}
                      data-testid="view-mode-trigger"
                    >
                      <currentConfig.icon className="h-3.5 w-3.5" />
                      <span className="text-[10px] font-medium hidden sm:block">{currentConfig.label}</span>
                      <ChevronDown className="h-2.5 w-2.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {Object.entries(VIEW_MODE_CONFIG).map(([mode, config]) => (
                      <DropdownMenuItem
                        key={mode}
                        onClick={() => setViewMode(mode)}
                        className={`flex items-center gap-2 ${effectiveView === mode ? 'bg-accent' : ''}`}
                      >
                        <config.icon className="h-4 w-4" />
                        {config.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 rounded-full p-0 bg-slate-100">
                    <User className="h-4 w-4 text-slate-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium">{user?.full_name || user?.email}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <ReportProblemButton />
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hamburger Menu Drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-50"
            />
            
            {/* Drawer */}
            <motion.div
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 w-80 bg-white shadow-2xl z-50 flex flex-col"
            >
              {/* Drawer Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <img src={MYEUROPACIFIC_LOGO} alt="myEuropacific" className="h-8" />
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-slate-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto py-4">
                <div className="px-4 mb-4">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
                    Click 📌 to pin to toolbar
                  </p>
                  <p className="text-xs text-slate-400">
                    Or drag items to the toolbar above
                  </p>
                </div>

                {Object.keys(CATEGORY_LABELS).filter(cat => modulesByCategory[cat]).map((category) => {
                  const modules = modulesByCategory[category];
                  const isExpanded = expandedCategories[category] !== false;
                  const categoryUnread = modules.reduce((n, m) => n + (unreadCounts[m.key] || 0), 0);
                  
                  // Default rendering for all categories (flat list)
                  return (
                  <div key={category} className="mb-4">
                    <button 
                      onClick={() => toggleCategory(category)}
                      className="w-full px-4 flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 hover:text-slate-700 transition-colors"
                      data-testid={`toggle-category-${category}`}
                    >
                      <span className="flex items-center gap-2">
                        {CATEGORY_LABELS[category] || category}
                        {!isExpanded && categoryUnread > 0 && (
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="Items need attention" />
                        )}
                      </span>
                      <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-0.5">
                            {modules.map((mod) => {
                              const Icon = mod.icon;
                              const isActive = location.pathname === mod.path;
                              const isPinned = pinnedModules.includes(mod.key);
                              const unreadCount = unreadCounts[mod.key] || 0;
                              return (
                                <div
                                  key={mod.key}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, mod.key)}
                                  className={`mx-2 flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-grab active:cursor-grabbing transition-colors ${
                                    isActive
                                      ? "bg-blue-50 text-blue-700"
                                      : "hover:bg-slate-50 text-slate-700"
                                  }`}
                                >
                                  <GripVertical className="h-4 w-4 text-slate-300" />
                                  <div className="relative">
                                    <Icon className="h-4 w-4" />
                                    {unreadCount > 0 && (
                                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                      </span>
                                    )}
                                  </div>
                                  <Link 
                                    to={mod.path} 
                                    onClick={() => setMenuOpen(false)}
                                    className="flex-1 font-medium text-sm"
                                  >
                                    {mod.label}
                                  </Link>
                                  {unreadCount > 0 && (
                                    <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                                      {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                  )}
                                  {/* Pin/Unpin button */}
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (isPinned) {
                                        savePinnedModules(pinnedModules.filter(k => k !== mod.key));
                                      } else if (pinnedModules.length < 8) {
                                        savePinnedModules([...pinnedModules, mod.key]);
                                      }
                                    }}
                                    className={`p-1 rounded hover:bg-slate-200 transition-colors ${isPinned ? 'text-blue-500' : 'text-slate-400 hover:text-slate-600'}`}
                                    title={isPinned ? "Unpin from toolbar" : "Pin to toolbar"}
                                  >
                                    <Pin className={`h-4 w-4 ${isPinned ? 'fill-blue-500' : ''}`} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  );
                })}

                {/* O365 Add-ins Section */}
                {(effectiveView === "staff" || effectiveView === "god") && (
                  <div className="mb-4 pb-4" data-testid="o365-addins-section">
                    <button 
                      onClick={() => toggleCategory('o365')}
                      className="w-full px-4 flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 hover:text-slate-700 transition-colors"
                      data-testid="toggle-category-o365"
                    >
                      <span>O365 ADD-INS</span>
                      <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expandedCategories.o365 ? 'rotate-90' : ''}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {expandedCategories.o365 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-0.5">
                            <Link
                              to="/outlook-download/operations"
                              onClick={() => setMenuOpen(false)}
                              className="mx-2 flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-slate-50 text-slate-700"
                              data-testid="operations-plugin-link"
                            >
                              <Download className="h-4 w-4" />
                              <span className="flex-1 font-medium text-sm">Operations Panel</span>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Outlook</span>
                            </Link>
                            <Link
                              to="/outlook-download/sales"
                              onClick={() => setMenuOpen(false)}
                              className="mx-2 flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-slate-50 text-slate-700"
                              data-testid="sales-plugin-link"
                            >
                              <Download className="h-4 w-4" />
                              <span className="flex-1 font-medium text-sm">Sales Panel</span>
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Outlook</span>
                            </Link>
                            <Link
                              to="/outlook-download/finance"
                              onClick={() => setMenuOpen(false)}
                              className="mx-2 flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-slate-50 text-slate-700"
                              data-testid="finance-plugin-link"
                            >
                              <Download className="h-4 w-4" />
                              <span className="flex-1 font-medium text-sm">Finance Panel</span>
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Outlook</span>
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Drawer Footer */}
              <div className="p-4 border-t bg-slate-50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{user?.full_name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer for fixed navbar */}
      <div className="h-12" />
    </>
  );
};
